import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { TeamEvent, TeamEventType, TeamUniform } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';
import { LocationPinPicker } from '../../components/LocationMap';

const TYPES: { value: TeamEventType; label: string }[] = [
  { value: 'GAME', label: 'Game' },
  { value: 'PRACTICE', label: 'Practice' },
  { value: 'TEAM_EVENT', label: 'Team event' },
];

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const repeatUntilIso = (date: string) =>
  new Date(`${date}T23:59:59`).toISOString();

const recurrenceLabel = (event: TeamEvent) => {
  if (!event.recurrence) return undefined;
  const frequency = event.recurrence.frequency.toLowerCase();
  const until = event.recurrence.until ? ` until ${new Date(event.recurrence.until).toLocaleDateString()}` : '';
  const count = event.recurrenceCount ? ` · ${event.recurrenceIndex !== undefined ? event.recurrenceIndex + 1 : 1}/${event.recurrenceCount}` : '';
  return `Repeats ${frequency}${until}${count}`;
};

export default function Calendar() {
  const { teamId } = useParams<{ teamId: string }>();
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [uniforms, setUniforms] = useState<TeamUniform[]>([]);
  const [selected, setSelected] = useState<TeamEvent>();
  const [matrix, setMatrix] = useState<Array<{ player: { name: string; jerseyNumber: string }; status: string }>>([]);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string>();
  const [form, setForm] = useState({
    type: 'PRACTICE' as TeamEventType,
    title: '',
    startAt: '',
    endAt: '',
    arrivalAt: '',
    warmupAt: '',
    warmupLocationName: '',
    warmupAddress: '',
    warmupLatitude: undefined as number | undefined,
    warmupLongitude: undefined as number | undefined,
    locationName: '',
    address: '',
    uniformId: '',
    notes: '',
    rsvpEnabled: true,
    notifyTeam: true,
    repeatsWeekly: false,
    repeatUntil: '',
  });

  const load = async () => {
    if (!teamId) return;
    try {
      const [eventRes, uniformRes] = await Promise.all([
        admin.listEvents(teamId),
        admin.listUniforms(teamId),
      ]);
      setEvents(eventRes.events);
      setUniforms(uniformRes.uniforms);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const upcoming = useMemo(
    () => events.filter((e) => e.status === 'SCHEDULED').sort((a, b) => a.startAt.localeCompare(b.startAt)),
    [events],
  );

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId) return;
    if (form.repeatsWeekly && !form.repeatUntil) {
      setError('Choose an end date for the recurring event.');
      return;
    }
    try {
      await admin.createEvent(teamId, {
        ...form,
        startAt: new Date(form.startAt).toISOString(),
        endAt: form.endAt ? new Date(form.endAt).toISOString() : undefined,
        arrivalAt: form.arrivalAt ? new Date(form.arrivalAt).toISOString() : undefined,
        warmupAt: form.warmupAt ? new Date(form.warmupAt).toISOString() : undefined,
        warmupLocationName: form.warmupLocationName || undefined,
        warmupAddress: form.warmupAddress || undefined,
        warmupLatitude: form.warmupLatitude,
        warmupLongitude: form.warmupLongitude,
        address: form.address || undefined,
        uniformId: form.uniformId || undefined,
        notifyTeam: form.notifyTeam,
        recurrence: form.repeatsWeekly
          ? {
              frequency: 'WEEKLY',
              interval: 1,
              until: repeatUntilIso(form.repeatUntil),
            }
          : undefined,
      });
      setShowNew(false);
      setForm({
        type: 'PRACTICE',
        title: '',
        startAt: '',
        endAt: '',
        arrivalAt: '',
        warmupAt: '',
        warmupLocationName: '',
        warmupAddress: '',
        warmupLatitude: undefined,
        warmupLongitude: undefined,
        locationName: '',
        address: '',
        uniformId: '',
        notes: '',
        rsvpEnabled: true,
        notifyTeam: true,
        repeatsWeekly: false,
        repeatUntil: '',
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const loadRsvps = async (event: TeamEvent) => {
    if (!teamId) return;
    setSelected(event);
    try {
      const res = await admin.listEventRsvps(teamId, event.eventId);
      setMatrix(res.matrix);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Calendar</Heading>
          <Text className="mt-1 text-sm">Games, practices, team events, arrival times, and RSVP tracking.</Text>
        </div>
        <Button color="dark/zinc" onClick={() => setShowNew((v) => !v)}>{showNew ? 'Cancel' : 'Add event'}</Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Upcoming" value={upcoming.length} tone="emerald" />
        <StatCard label="Games" value={events.filter((e) => e.type === 'GAME').length} />
        <StatCard label="RSVP enabled" value={events.filter((e) => e.rsvpEnabled).length} tone="amber" />
      </div>

      {showNew ? (
        <Panel>
          <form onSubmit={create} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <Label>Type</Label>
                <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as TeamEventType })}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </Select>
              </Field>
              <Field className="sm:col-span-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <Label>Start</Label>
                <Input type="datetime-local" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} required />
              </Field>
              <Field>
                <Label>Arrival</Label>
                <Input type="datetime-local" value={form.arrivalAt} onChange={(e) => setForm({ ...form, arrivalAt: e.target.value })} />
              </Field>
              <Field>
                <Label>End</Label>
                <Input type="datetime-local" value={form.endAt} onChange={(e) => setForm({ ...form, endAt: e.target.value })} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field>
                <Label>Uniform</Label>
                <Select value={form.uniformId} onChange={(e) => setForm({ ...form, uniformId: e.target.value })}>
                  <option value="">— None —</option>
                  {uniforms.filter((u) => u.active).map((uniform) => (
                    <option key={uniform.uniformId} value={uniform.uniformId}>{uniform.name}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Warmup time</Label>
                <Input type="datetime-local" value={form.warmupAt} onChange={(e) => setForm({ ...form, warmupAt: e.target.value })} />
              </Field>
              <Field>
                <Label>Warmup place</Label>
                <Input value={form.warmupLocationName} onChange={(e) => setForm({ ...form, warmupLocationName: e.target.value })} />
              </Field>
            </div>
            <Field>
              <Label>Warmup address</Label>
              <Input value={form.warmupAddress} onChange={(e) => setForm({ ...form, warmupAddress: e.target.value })} />
            </Field>
            <Field className="space-y-2">
              <Label>Warmup pin</Label>
              <LocationPinPicker
                value={{ latitude: form.warmupLatitude, longitude: form.warmupLongitude }}
                onChange={(pin) => setForm({ ...form, warmupLatitude: pin.latitude, warmupLongitude: pin.longitude })}
              />
              {form.warmupLatitude !== undefined && form.warmupLongitude !== undefined ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Text className="font-mono text-xs">{form.warmupLatitude}, {form.warmupLongitude}</Text>
                  <Button
                    type="button"
                    outline
                    onClick={() => setForm({ ...form, warmupLatitude: undefined, warmupLongitude: undefined })}
                  >
                    Clear pin
                  </Button>
                </div>
              ) : null}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <Label>Location</Label>
                <Input value={form.locationName} onChange={(e) => setForm({ ...form, locationName: e.target.value })} />
              </Field>
              <Field>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
            </div>
            <Field>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <CheckboxField>
                <Checkbox checked={form.rsvpEnabled} onChange={(checked) => setForm({ ...form, rsvpEnabled: checked })} />
                <Label>Collect RSVP responses</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox checked={form.notifyTeam} onChange={(checked) => setForm({ ...form, notifyTeam: checked })} />
                <Label>Notify team</Label>
              </CheckboxField>
              <CheckboxField>
                <Checkbox checked={form.repeatsWeekly} onChange={(checked) => setForm({ ...form, repeatsWeekly: checked })} />
                <Label>Repeat weekly</Label>
              </CheckboxField>
              {form.repeatsWeekly ? (
                <Field>
                  <Label>End date</Label>
                  <Input type="date" value={form.repeatUntil} onChange={(e) => setForm({ ...form, repeatUntil: e.target.value })} required />
                </Field>
              ) : null}
            </div>
            <Button type="submit" color="dark/zinc">Create event</Button>
          </form>
        </Panel>
      ) : null}

      {error ? <Text className="text-red-400">{error}</Text> : null}

      {events.length === 0 ? (
        <EmptyState title="No calendar events yet" body="Scheduled games will also create calendar rows automatically." />
      ) : (
        <ul className="space-y-3">
          {events.map((e) => (
            <li key={e.eventId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-zinc-100">{e.title}</div>
                    <Badge color={e.type === 'GAME' ? 'red' : e.type === 'PRACTICE' ? 'sky' : 'zinc'}>{e.type.replace('_', ' ')}</Badge>
                    {e.rsvpEnabled ? <Badge color="amber">RSVP</Badge> : null}
                  </div>
                  <Text className="mt-1 text-sm">{fmt(e.startAt)}{e.arrivalAt ? ` · arrive ${fmt(e.arrivalAt)}` : ''}{e.locationName ? ` · ${e.locationName}` : ''}</Text>
                  {e.warmupAt || e.warmupLocationName || e.uniform ? (
                    <Text className="mt-1 text-sm">
                      {e.warmupAt ? `Warmup ${fmt(e.warmupAt)}` : ''}
                      {e.warmupLocationName ? `${e.warmupAt ? ' · ' : ''}${e.warmupLocationName}` : ''}
                      {e.uniform ? `${e.warmupAt || e.warmupLocationName ? ' · ' : ''}${e.uniform.name}` : ''}
                    </Text>
                  ) : null}
                  {recurrenceLabel(e) ? <Text className="mt-1 text-xs">{recurrenceLabel(e)}</Text> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button href={`/admin/teams/${teamId}/events/${e.eventId}`} outline>Details</Button>
                  <Button outline onClick={() => void loadRsvps(e)}>RSVP matrix</Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {selected ? (
        <Panel>
          <Heading level={3}>{selected.title} RSVP</Heading>
          {matrix.length === 0 ? <Text className="mt-2 text-sm">No player RSVP responses yet.</Text> : (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {matrix.map((row) => (
                <li key={`${row.player.jerseyNumber}-${row.player.name}`} className="rounded border border-zinc-800 bg-zinc-950 p-3 text-sm">
                  #{row.player.jerseyNumber} {row.player.name} · {row.status}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : null}
    </div>
  );
}
